import React from 'react';
import { TeleprompterMode } from '@usacsmideo/types';

interface TeleprompterModeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentMode: TeleprompterMode;
  onModeChange: (mode: TeleprompterMode) => void;
}

export const TeleprompterModeModal: React.FC<TeleprompterModeModalProps> = ({
  isOpen,
  onClose,
  currentMode,
  onModeChange,
}) => {
  const [selectedMode, setSelectedMode] = React.useState<TeleprompterMode>(currentMode);

  React.useEffect(() => {
    setSelectedMode(currentMode);
  }, [currentMode, isOpen]);

  if (!isOpen) return null;

  const handleApply = () => {
    onModeChange(selectedMode);
    onClose();
  };

  const handleCancel = () => {
    setSelectedMode(currentMode);
    onClose();
  };

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black" onClick={handleCancel}>
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-4 text-2xl font-bold">Select Display Mode</h2>

        <div className="mb-6 space-y-4">
          {/* Auto-scroll mode */}
          <label className="flex cursor-pointer items-start space-x-3">
            <input
              type="radio"
              name="mode"
              value="auto-scroll"
              checked={selectedMode === 'auto-scroll'}
              onChange={(e) => setSelectedMode(e.target.value as TeleprompterMode)}
              className="mt-1"
            />
            <div>
              <div className="font-semibold">Auto-scroll</div>
              <div className="text-sm text-gray-600">Text automatically scrolls at your chosen speed</div>
            </div>
          </label>

          {/* Manual-scroll mode */}
          <label className="flex cursor-pointer items-start space-x-3">
            <input
              type="radio"
              name="mode"
              value="manual-scroll"
              checked={selectedMode === 'manual-scroll'}
              onChange={(e) => setSelectedMode(e.target.value as TeleprompterMode)}
              className="mt-1"
            />
            <div>
              <div className="font-semibold">Manual-scroll</div>
              <div className="text-sm text-gray-600">
                Drag text up/down with mouse or touch, click/hold arrow buttons (or use up/down arrow keys on desktop)
              </div>
            </div>
          </label>

          {/* Slide-advance mode */}
          <label className="flex cursor-pointer items-start space-x-3">
            <input
              type="radio"
              name="mode"
              value="slide-advance"
              checked={selectedMode === 'slide-advance'}
              onChange={(e) => setSelectedMode(e.target.value as TeleprompterMode)}
              className="mt-1"
            />
            <div>
              <div className="font-semibold">Slide-advance</div>
              <div className="text-sm text-gray-600">
                Navigate through script line-by-line with buttons (or use arrow keys on desktop)
              </div>
            </div>
          </label>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="rounded bg-gray-200 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="rounded bg-[#730D0E] px-4 py-2 text-white transition-colors hover:bg-[#730D0E]/90"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};
